import type { Doc } from '../editor/editor';
import type { PageSize } from '../editor/pageFormat';
import { renderThumbnail } from '../export/render';

/*
 * Preview pictures of pages, shared by the page strip (src/ui/pageStrip.ts) and the "หน้าทั้งหมด" panel
 * (src/ui/pagesSheet.ts). Keyed by the page's size and content, so an unchanged page is never drawn twice; drawn one
 * at a time (each one loads the page's pictures into a canvas of its own — several at once is a lot for an iPad).
 */

const done = new Map<string, string>();
const waiting = new Map<string, Promise<string>>();
let queue: Promise<unknown> = Promise.resolve();
/** about 50 pages × a few versions; the least recently used are dropped (drawn again if needed) */
const KEEP = 160;

/**
 * a short key for a page's content: two 32-bit hashes and the length (the JSON itself as the key kept tens of MB
 * alive with 50 pages); a mix-up would need both hashes and the length to match
 */
export function contentKey(json: string): string {
  let a = 0x811c9dc5, b = 5381;
  for (let i = 0; i < json.length; i++) {
    const c = json.charCodeAt(i);
    a = Math.imul(a ^ c, 0x01000193);
    b = (Math.imul(b, 33) + c) | 0;
  }
  return `${(a >>> 0).toString(36)}.${(b >>> 0).toString(36)}.${json.length}`;
}
const keyOf = (json: string, size: PageSize, side: number) => `${side}|${size.w}x${size.h}|${contentKey(json)}`;

/** the preview if it is already drawn (and now the most recently used: the least used are dropped first) */
export function readyThumb(json: string, size: PageSize, side: number): string | undefined {
  const key = keyOf(json, size, side), url = done.get(key);
  if (url !== undefined) { done.delete(key); done.set(key, url); }
  return url;
}

/** the preview of a page (data URL); throws with a Thai reason when it cannot be drawn */
export function pageThumb(json: string, size: PageSize, doc: Doc, side: number): Promise<string> {
  const key = keyOf(json, size, side);
  const have = readyThumb(json, size, side);
  if (have) return Promise.resolve(have);
  const busy = waiting.get(key);
  if (busy) return busy;
  const job = queue.then(() => renderThumbnail(json, { ...doc, ...size }, side)).then(url => {
    done.set(key, url);
    if (done.size > KEEP) done.delete(done.keys().next().value!);
    return url;
  }).finally(() => waiting.delete(key));
  queue = job.catch(() => {});
  waiting.set(key, job);
  return job;
}
